import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';

const DeleteAllFilesConfirmModal = ({
  open,
  count,
  fileTypeLabels,
  fileFormats,
  gameName,
  loading,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  count: number;
  // Labels of the File Types in the filter; empty when it has none.
  fileTypeLabels: ReactNode[];
  // File formats in the filter; empty when it has none.
  fileFormats: string[];
  // The game, never one of its RTP versions - the files go from all of them.
  gameName: string;
  loading?: boolean;
  error?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const intl = useIntl();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      isActionBusy={loading}
      title={
        <FormattedMessage
          id="deleteAllFilesTitle"
          defaultMessage="Delete all files"
        />
      }
      actionsError={
        error ? (
          <FormattedMessage
            id="filesNotDeletedTryAgain"
            defaultMessage="The files were not deleted. Try again."
          />
        ) : null
      }
      actions={
        <>
          <Button
            variant="text"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cancel Button"
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            loading={loading}
            aria-label="Delete all Button"
          >
            <FormattedMessage
              id="deleteAll"
              defaultMessage="Delete all"
            />
          </Button>
        </>
      }
    >
      <FormattedMessage
        id="deleteAllFilesConfirmMessage"
        defaultMessage="This deletes all {files}{formats} for {gameName}, across every RTP version of the game. This cannot be undone."
        values={{
          files:
            fileTypeLabels.length > 0 ? (
              <FormattedMessage
                id="deleteAllFilesOfTypes"
                defaultMessage="{count, plural, one {# {fileTypes} file} other {# {fileTypes} files}}"
                values={{
                  count,
                  fileTypes: intl.formatList(fileTypeLabels, {
                    type: 'conjunction',
                  }),
                }}
              />
            ) : (
              <FormattedMessage
                id="deleteAllFilesCount"
                defaultMessage="{count, plural, one {# file} other {# files}}"
                values={{ count }}
              />
            ),
          formats:
            fileFormats.length > 0 ? (
              <FormattedMessage
                id="deleteAllFilesInFormats"
                defaultMessage=" in {formats} format"
                values={{
                  formats: intl.formatList(fileFormats, {
                    type: 'disjunction',
                  }),
                }}
              />
            ) : null,
          gameName,
        }}
      />
    </Modal>
  );
};

export default DeleteAllFilesConfirmModal;
